import type { Feature, FeatureCollection, LineString, Position } from "geojson";

/** Haversine en metros */
function haversine(a: Position, b: Position): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad((b[1] as number) - (a[1] as number));
    const dLng = toRad((b[0] as number) - (a[0] as number));
    const lat1 = toRad(a[1] as number);
    const lat2 = toRad(b[1] as number);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.sqrt(h));
}

/** Toma coords y devuelve subset por paso (cada stepMeters aprox) + límite de longitud total */
function resampleByStepLimit(coords: Position[], stepMeters: number, maxMeters: number): Position[] {
    if (coords.length < 2) return coords.slice();
    const out: Position[] = [];
    let accu = 0, carry = 0;
    out.push(coords[0]);

    for (let i = 1; i < coords.length; i++) {
        const A = coords[i - 1], B = coords[i];
        const seg = haversine(A, B);
        let distFromA = stepMeters - carry;

        while (distFromA <= seg) {
            const t = distFromA / seg;
            const lng = (A[0] as number) + ((B[0] as number) - (A[0] as number)) * t;
            const lat = (A[1] as number) + ((B[1] as number) - (A[1] as number)) * t;
            out.push([lng, lat]);
            distFromA += stepMeters;
            accu += stepMeters;
            if (accu >= maxMeters) return out;
        }
        carry = seg - (distFromA - stepMeters);
        if (carry < 1e-6) carry = 0;
        accu += seg;
        if (accu >= maxMeters) return out;
    }
    if (out[out.length - 1] !== coords[coords.length - 1]) {
        out.push(coords[coords.length - 1]);
    }
    return out;
}

/** Catmull-Rom Spline 2D → devuelve puntos suavizados */
function catmullRom(points: Position[], segmentsPerSpan = 16, tension = 0.5): Position[] {
    if (points.length < 2) return points.slice();
    const res: Position[] = [];
    const P = (i: number) => points[Math.max(0, Math.min(points.length - 1, i))];

    for (let i = 0; i < points.length - 1; i++) {
        const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2);
        for (let j = 0; j < segmentsPerSpan; j++) {
            const t = j / segmentsPerSpan;
            const t2 = t * t, t3 = t2 * t;
            const m0x = ((p2[0] as number) - (p0[0] as number)) * tension;
            const m0y = ((p2[1] as number) - (p0[1] as number)) * tension;
            const m1x = ((p3[0] as number) - (p1[0] as number)) * tension;
            const m1y = ((p3[1] as number) - (p1[1] as number)) * tension;

            const a0x = 2 * (p1[0] as number) - 2 * (p2[0] as number) + m0x + m1x;
            const a0y = 2 * (p1[1] as number) - 2 * (p2[1] as number) + m0y + m1y;
            const a1x = -3 * (p1[0] as number) + 3 * (p2[0] as number) - 2 * m0x - m1x;
            const a1y = -3 * (p1[1] as number) + 3 * (p2[1] as number) - 2 * m0y - m1y;

            const x = a0x * t3 + a1x * t2 + m0x * t + (p1[0] as number);
            const y = a0y * t3 + a1y * t2 + m0y * t + (p1[1] as number);
            res.push([x, y]);
        }
    }
    res.push(points[points.length - 1]);
    return res;
}

/** Extrae todas las coordenadas de líneas del FC */
function extractLineCoords(fc: FeatureCollection): Position[] {
    const out: Position[] = [];

    for (const f of fc.features) {
        const g = (f.geometry || {}) as GeoJSON.Geometry;
        if (!g) continue;

        switch (g.type) {
            case "LineString":
                out.push(...g.coordinates as Position[]);
                break;
            case "MultiLineString":
                for (const line of g.coordinates as Position[][]) out.push(...line);
                break;
            default:
                // Ignora otros tipos (Point, Polygon, etc.)
                break;
        }
    }

    return out;
}

export type SyntheticOptions = {
    stepMeters?: number;      // espaciado para muestreo base (default 5000 m)
    maxMeters?: number;       // longitud máxima de la ruta sintética (default 120000 m)
    segmentsPerSpan?: number; // densidad del spline (default 12)
    tension?: number;         // 0..1, suavidad (default 0.5)
};

/** Construye una ruta propia: más corta y suave a partir de la original */
export function buildSyntheticRoute(
    original: FeatureCollection,
    opts: SyntheticOptions = {}
): FeatureCollection {
    const {
        stepMeters = 5000,
        maxMeters = 120000,
        segmentsPerSpan = 12,
        tension = 0.5,
    } = opts;

    const coords = extractLineCoords(original);
    if (coords.length < 2) {
        return original;
    }

    // 1) resample + limit
    const coarse = resampleByStepLimit(coords, stepMeters, maxMeters);
    // 2) suavizado spline
    const smooth = catmullRom(coarse, segmentsPerSpan, tension);

    const line: Feature<LineString> = {
        type: "Feature",
        geometry: { type: "LineString", coordinates: smooth },
        properties: { kind: "synthetic_route" },
    };
    return { type: "FeatureCollection", features: [line] };
}